// HydraDrawer — drawer flutuante reutilizável pros assistentes IA da Hydrodata.
// Default: persona Dwight (comercial). Pode ser parametrizado pra outros
// agentes via props (Jim no operacional, etc.). Filtra conversas por agente.
import {
  useEffect, useLayoutEffect, useRef, useState,
  type CSSProperties, type ReactNode, type DragEvent,
} from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';
import IconDwight from './IconDwight';
import { useIsMobile } from '../hooks/useResponsive';
import {
  resumirAtualizacaoProposta, resumirCamposAtualizados, formatarDuracao, descreverParada,
  textoPedirDeNovo, descreverPassoDeControle, filtrarAcoesVisiveis, situacaoAplicando, textoAplicacaoSemConfirmacao,
  ehPreviaCriacaoComposicao, formatarDinheiroComposicao, formatarNumeroComposicao,
  resumirCriacaoComposicao, resumirNegocioCriado, type PreviaCriacaoComposicao,
  descreverAlvoProposta, rotuloBloqueio,
} from '../utils/hydraCards';

// Configuração visual + comportamental do agente. Default = Dwight.
export interface AgenteConfig {
  agente: 'dwight' | 'jim' | 'pam';
  nome: string;
  subtitulo: string;
  corPrimaria: string;        // cor sólida (header text/buttons)
  gradiente: string;          // CSS gradient (header background)
  bubbleGradiente: string;    // CSS gradient do bubble flutuante
  bubbleSombra: string;       // box-shadow do bubble
  icone: ReactNode;           // ícone do bubble (size 28)
  iconeHeader: ReactNode;     // ícone do header (size 22)
  iconeVazio: ReactNode;      // ícone na tela "Olá" (size 42)
  dicaTitulo: string;
  dicaTexto: string;
  exemplos: string[];
  placeholder: string;
}

const AGENTE_DWIGHT: AgenteConfig = {
  agente: 'dwight',
  nome: 'Dwight',
  subtitulo: 'Assistente comercial · Claude Sonnet 4.5',
  corPrimaria: '#0930D0',
  gradiente: 'linear-gradient(135deg, #020D7F 0%, #0930D0 100%)',
  bubbleGradiente: 'linear-gradient(135deg, #0930D0 0%, #02B5DB 100%)',
  bubbleSombra: '0 4px 16px rgba(9,48,208,0.35)',
  icone: <IconDwight size={28} color="#fff" />,
  iconeHeader: <IconDwight size={22} color="#fff" />,
  iconeVazio: <IconDwight size={42} />,
  dicaTitulo: 'Olá! Sou o Dwight.',
  dicaTexto: 'Cole um briefing, encaminhe um e-mail, ou anexe um PDF.\nEu organizo no CRM pra você.',
  exemplos: [
    '"Recebi um pedido de proposta da Engevix pra estudo hidrológico em Santa Maria, 80 mil"',
    'Anexar um PDF de briefing',
    '"Marca uma atividade no negócio do consórcio Amarópolis: ligar pro engenheiro Carlos sexta"',
  ],
  placeholder: 'Cole texto, descreva uma situação ou anexe PDF...',
};

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Conversa {
  id: string;
  titulo: string;
  criado_em: string;
  atualizado_em: string;
  n_mensagens: number;
}

interface Mensagem {
  id: string;
  papel: 'user' | 'assistant';
  conteudo: string;
  anexos: any[];
  acoes_propostas: AcaoProposta[];
  criado_em: string;
  // Metadata de transparência (Onda visibilidade)
  passos?: any[];
  duracao_ms?: number | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
}

interface AcaoProposta {
  id: string;
  tool: string;
  input: any;
  // truncada = a resposta do modelo passou do limite de tamanho e esta ação
  // chegou pela metade; recuperada = outra ação íntegra a substituiu; invalida
  // = faltou campo obrigatório. Nenhuma delas tem botão Aplicar.
  // aplicando = reservado pelo servidor enquanto grava (situacaoAplicando).
  status: 'pending' | 'applied' | 'dismissed' | 'erro' | 'truncada' | 'recuperada' | 'invalida' | 'aplicando';
  aplicando_desde?: string;
  resultado?: any;
  motivo?: string;
  previa?: any;                        // atualizar_proposta_documento: tamanhos antes → depois
  numero_proposta_inferido?: boolean;  // numero_proposta veio da conversa, não do modelo
  candidatos_numero?: string[];        // invalida por ambiguidade: as propostas que apareceram na conversa
  input_parcial?: boolean;
  recuperada_por?: string[];
  recuperou_truncamento?: boolean;
  recuperou_acoes?: string[];
  // Edição de proposta consolidada (14/09/2026): um card por proposta. `origens`
  // diz qual chamada do modelo trouxe qual campo; motivo_codigo explica o bloqueio.
  origens?: { tool_use_id: string; campos: string[] }[];
  // Proposta certa (15/09/2026): numero_sem_origem | negocio_divergente | sem_precondicao.
  motivo_codigo?: 'proposta_mudou' | 'previa_indisponivel' | 'numero_sem_origem' | 'negocio_divergente' | 'sem_precondicao' | string;
  // Documento exato que o card mostra: o aplicar pela tela manda o id (alvo_visto).
  precondicao_proposta?: { proposta_gerada_id?: string; numero_proposta?: string; revisao_token?: string | null };
}

// Edição de proposta NUNCA entra no "aplicar todas": um clique não pode
// sobrescrever a descrição técnica sem ninguém ter lido o que muda (04/09/2026).
const TOOLS_EDICAO_PROPOSTA = new Set(['atualizar_proposta_documento', 'editar_trecho_proposta']);
// "Tentar de novo" só para o que pode repetir sem duplicar. gerar_proposta e
// criar_* com erro DEPOIS da escrita criariam uma segunda proposta/empresa —
// para essas, o caminho é pedir de novo ao Dwight (ele relê o estado).
const TOOLS_RETENTAVEIS = new Set([
  'atualizar_proposta_documento', 'editar_trecho_proposta', 'atualizar_negocio', 'atualizar_contato',
  'salvar_memoria', 'salvar_exemplo_redacao', 'vincular_composicao_a_proposta', 'marcar_licitacao',
]);

export default function HydraDrawer({ config = AGENTE_DWIGHT }: { config?: AgenteConfig } = {}) {
  const isMobile = useIsMobile();
  const [aberto, setAberto] = useState(false);
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [conversaAtiva, setConversaAtiva] = useState<string | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [anexos, setAnexos] = useState<{ nome: string; tipo: string; tamanho: number; file: File }[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);      // overlay de drag-and-drop
  const dragCounter = useRef(0);                             // conta enter/leave (evita flicker nos filhos)
  const [showSidebar, setShowSidebar] = useState(false);
  const [tempoEnvio, setTempoEnvio] = useState(0);  // contador em segundos enquanto enviando
  const cancelarRef = useRef(false);                  // flag pra interromper polling local
  const aplicandoRef = useRef<Set<string>>(new Set()); // cards com aplicação em andamento
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);  // auto-resize do composer

  // Contador que avança a cada segundo enquanto enviando=true
  useEffect(() => {
    if (!enviando) { setTempoEnvio(0); return; }
    const start = Date.now();
    const intervalo = setInterval(() => {
      setTempoEnvio(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(intervalo);
  }, [enviando]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (aberto) carregarConversas(); }, [aberto]);
  useEffect(() => { if (conversaAtiva) carregarMensagens(conversaAtiva); }, [conversaAtiva]);
  // Card reservado ('aplicando') por outro pedido — outra aba, o Telegram: sem
  // reler, ele ficava "⏳ aplicando…" até alguém recarregar a página.
  useEffect(() => {
    if (!aberto || !conversaAtiva || enviando) return;
    const temReserva = mensagens.some(m => (m.acoes_propostas || []).some(a => situacaoAplicando(a) === 'em_andamento'));
    if (!temReserva) return;
    const t = setTimeout(() => { carregarMensagens(conversaAtiva); }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensagens, conversaAtiva, aberto, enviando]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensagens, enviando]);

  // Auto-resize do composer: cresce conforme o texto, com teto de 40% do drawer
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = Math.max(window.innerHeight * 0.4, 200);  // ~40vh ou 200px (o que for maior)
    const desired = Math.min(el.scrollHeight, max);
    el.style.height = desired + 'px';
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  }, [input, aberto]);

  // Listener global pra atalho "Novo briefing" — qualquer tela pode disparar
  // window.dispatchEvent(new CustomEvent('dwight:novo-briefing')) e o drawer
  // abre, cria nova conversa e pré-popula um prompt de absorção de briefing.
  useEffect(() => {
    async function abrirBriefing() {
      setAberto(true);
      try { await novaConversa(); } catch {/* segue mesmo se falhar */}
      setInput(
        'Recebi um novo briefing de cliente. Vou colar o texto / anexar PDFs e KMZ a seguir.\n' +
        'Por favor, extraia: cliente (nome/CNPJ), contato (nome/email/telefone), área técnica, ' +
        'valor estimado, escopo resumido. Em seguida, proponha:\n' +
        '  1. criar_negocio com esses dados\n' +
        '  2. atividades iniciais (analisar briefing, retornar contato)\n' +
        '  3. composição-esboço se houver dados suficientes para estimar.'
      );
      // Foca o textarea pro usuário começar a colar/anexar imediatamente
      setTimeout(() => { textareaRef.current?.focus(); }, 200);
    }
    window.addEventListener('dwight:novo-briefing', abrirBriefing);
    return () => window.removeEventListener('dwight:novo-briefing', abrirBriefing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function carregarConversas() {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/agentes/conversas?agente=${config.agente}`,
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setConversas(Array.isArray(data) ? data : []);
      // Se não há conversa ativa e há conversas, abre a mais recente
      if (!conversaAtiva && Array.isArray(data) && data.length > 0) setConversaAtiva(data[0].id);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    }
  }

  async function carregarMensagens(id: string) {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/agentes/conversas/${id}`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setMensagens(Array.isArray(data.mensagens) ? data.mensagens : []);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    }
  }

  async function novaConversa() {
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/agentes/conversas`,
        { titulo: 'Nova conversa', agente: config.agente },
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setConversaAtiva(data.id);
      setMensagens([]);
      await carregarConversas();
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
  }

  async function excluirConversa(id: string) {
    if (!window.confirm('Excluir esta conversa?')) return;
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/agentes/conversas/${id}`, { headers: { Authorization: token } });
      if (conversaAtiva === id) {
        setConversaAtiva(null);
        setMensagens([]);
      }
      await carregarConversas();
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
  }

  async function onAnexar(files: FileList | null) {
    if (!files || files.length === 0) return;
    // Guarda o próprio File — o upload vai direto pro S3 (URL assinada) na hora de
    // enviar, sem base64 no corpo do POST (que estourava o limite de 413).
    const novos: { nome: string; tipo: string; tamanho: number; file: File }[] = [];
    for (const f of Array.from(files)) {
      // Aceita PDF, imagem, e arquivos geo (KML/KMZ/SHP zip/GeoJSON).
      const ehAceito = /\.(pdf|kml|kmz|zip|geojson|json|png|jpe?g|gif|webp)$/i.test(f.name)
        || f.type.startsWith('image/') || f.type === 'application/pdf';
      if (!ehAceito) {
        setErro(`Arquivo "${f.name}" não é aceito. Tipos: PDF, KML/KMZ, Shapefile (.zip), GeoJSON e imagens.`);
        continue;
      }
      // limite ~25MB por arquivo (KMZs e shapefiles podem ser maiores que PDFs)
      if (f.size > 25 * 1024 * 1024) {
        setErro(`Arquivo "${f.name}" muito grande (>25MB).`);
        continue;
      }
      novos.push({ nome: f.name, tipo: f.type || 'application/octet-stream', tamanho: f.size, file: f });
    }
    setAnexos(prev => [...prev, ...novos]);
  }

  // Drag-and-drop de arquivos direto na área de chat (mesma validação da onAnexar).
  // dragCounter conta enter/leave pra não piscar o overlay ao passar sobre filhos.
  function onDragEnter(e: DragEvent) {
    if (enviando || !e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault(); dragCounter.current++; setArrastando(true);
  }
  function onDragOver(e: DragEvent) {
    if (arrastando) e.preventDefault();  // permite o drop
  }
  function onDragLeave(e: DragEvent) {
    if (!arrastando) return;
    e.preventDefault(); dragCounter.current--;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setArrastando(false); }
  }
  function onDrop(e: DragEvent) {
    if (!arrastando) return;
    e.preventDefault(); dragCounter.current = 0; setArrastando(false);
    if (!enviando) onAnexar(e.dataTransfer.files);
  }

  async function enviar() {
    const texto = input.trim();
    if (!texto && anexos.length === 0) return;
    setErro(null);
    let convId = conversaAtiva;
    if (!convId) {
      // Cria conversa antes
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/agentes/conversas`,
          { titulo: texto.slice(0, 60) || 'Nova conversa', agente: config.agente },
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        convId = data.id;
        setConversaAtiva(data.id);
      } catch (e: any) { setErro(e.response?.data?.error || e.message); return; }
    }

    setEnviando(true);
    // Backup pra restaurar se der erro (não perde digitação ou anexos do user)
    const textoBackup = texto;
    const anexosEnvio = anexos;
    setInput('');
    setAnexos([]);

    // Adiciona mensagem do user otimisticamente
    const tempId = 'tmp-' + Date.now();
    setMensagens(prev => [...prev, {
      id: tempId, papel: 'user', conteudo: texto,
      anexos: anexosEnvio.map(a => ({ nome: a.nome, tipo: a.tipo })),
      acoes_propostas: [], criado_em: new Date().toISOString(),
    }]);

    try {
      const token = await getToken();
      // 1) Sobe cada anexo direto pro S3 via URL assinada (evita o 413 no POST da
      //    mensagem). Manda pro Dwight só a referência (s3_key), sem o arquivo no corpo.
      const anexosRef: { nome: string; tipo: string; tamanho: number; s3_key: string }[] = [];
      for (const a of anexosEnvio) {
        const du = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'chat_anexo_upload_url', filename: a.nome, content_type: a.tipo, conversa_id: convId },
          { headers: { Authorization: token } });
        const d = du.data.body ? JSON.parse(du.data.body) : du.data;
        await axios.put(d.upload_url, a.file, { headers: { 'Content-Type': a.tipo }, timeout: 120000 });
        anexosRef.push({ nome: a.nome, tipo: a.tipo, tamanho: a.tamanho, s3_key: d.s3_key });
      }
      // 2) POST da mensagem (corpo pequeno agora — só texto + refs). Retorna 202 (async).
      const r = await axios.post(`${API_URL}/agentes/conversas/${convId}/mensagens`,
        { texto, anexos: anexosRef },
        { headers: { Authorization: token }, timeout: 90000 }
      );
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      // Recarrega mensagens (já mostra a do user + qualquer histórico)
      await carregarMensagens(convId!);

      if (data?.processando && data?.mensagem_usuario) {
        // Polling até o Dwight responder OU usuário clicar Parar
        cancelarRef.current = false;
        const userTs = new Date(data.mensagem_usuario.criado_em).getTime();
        const startedAt = Date.now();
        // A Lambda do Dwight tem 300 s (era 120 s): esperar menos que isso faz
        // o drawer dizer "demorou demais" com a resposta ainda a caminho.
        const MAX_WAIT_MS = 320000;
        // Depois de 1 min o intervalo dobra — cada poll traz a conversa inteira.
        const intervaloPoll = () => (Date.now() - startedAt > 60000 ? 5000 : 2500);
        let achou = false;
        while (Date.now() - startedAt < MAX_WAIT_MS && !cancelarRef.current) {
          await new Promise(res => setTimeout(res, intervaloPoll()));
          if (cancelarRef.current) break;
          try {
            const t2 = await getToken();
            const res = await axios.get(`${API_URL}/agentes/conversas/${convId}`, {
              headers: { Authorization: t2 }, timeout: 15000,
            });
            const conv = res.data.body ? JSON.parse(res.data.body) : res.data;
            const msgs = conv.mensagens || [];
            const novaResposta = msgs.find((m: any) =>
              m.papel === 'assistant' && new Date(m.criado_em).getTime() > userTs
            );
            if (novaResposta) {
              setMensagens(msgs);
              achou = true;
              break;
            }
          } catch { /* ignora erro transiente, continua polling */ }
        }
        if (cancelarRef.current) {
          // Cancelamento local — não mostra erro
        } else if (!achou) {
          setErro('O Dwight demorou demais pra responder. A resposta pode chegar — atualize a conversa em alguns instantes.');
        }
      } else if (data?.mensagem_assistente) {
        // Resposta veio síncrona (fallback) — recarrega
        await carregarMensagens(convId!);
      }
      await carregarConversas();
    } catch (e: any) {
      const msg = e.code === 'ECONNABORTED'
        ? `Tempo esgotado ao enviar (${anexosEnvio.length > 0 ? 'PDF muito grande?' : 'rede instável?'}). Seu texto e anexos foram preservados — ajuste e reenvie.`
        : (e.response?.data?.error || e.message);
      setErro(msg);
      // Remove a msg otimística (não foi salva no DB)
      setMensagens(prev => prev.filter(m => m.id !== tempId));
      // RESTAURA o que estava no input + anexos pra usuário não perder o que digitou
      setInput(textoBackup);
      setAnexos(anexosEnvio);
    } finally { setEnviando(false); }
  }

  async function pararDwight() {
    if (!conversaAtiva) return;
    cancelarRef.current = true;  // para o polling local imediatamente
    try {
      const token = await getToken();
      // Cancelamento via DELETE com query (reutiliza resource já existente — economiza CFN resources)
      await axios.delete(`${API_URL}/agentes/conversas/${conversaAtiva}?apenas_cancelar=true`,
        { headers: { Authorization: token } });
    } catch { /* mesmo se falhar, ainda paramos local */ }
    setEnviando(false);
    setErro(null);
    if (conversaAtiva) await carregarMensagens(conversaAtiva);
  }

  // `confirmado` = clique individual no card (a pessoa leu a prévia). O backend
  // exige isso para edição de proposta; "aplicar todas" nunca manda.
  // `statusEsperado` = o status que a pessoa estava vendo: se o card mudou em outra
  // aba ou no Telegram, o servidor recusa em vez de repetir (trava do Aplicar).
  async function aplicarAcao(mensagemId: string, acaoId: string, confirmado = true, statusEsperado?: string) {
    // Duplo clique no card não pode aplicar duas vezes: "Gerar proposta" e
    // "Criar composição" levam segundos e o servidor só vê o status depois.
    const chave = `${mensagemId}:${acaoId}`;
    if (aplicandoRef.current.has(chave)) return;
    aplicandoRef.current.add(chave);
    try {
      const token = await getToken();
      // Proposta certa (15/09/2026): o servidor só aplica edição/vínculo de proposta
      // se a tela disser QUAL documento o card mostrou. Aba antiga não manda.
      const acaoVista = mensagens.find(m => m.id === mensagemId)?.acoes_propostas?.find((a: AcaoProposta) => a.id === acaoId);
      const alvoVisto = acaoVista?.precondicao_proposta?.proposta_gerada_id;
      const r = await axios.post(`${API_URL}/agentes/acoes/aplicar`,
        { mensagem_id: mensagemId, acao_id: acaoId, confirmado, ...(statusEsperado ? { status_esperado: statusEsperado } : {}),
          ...(alvoVisto ? { alvo_visto: alvoVisto } : {}) },
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (conversaAtiva) await carregarMensagens(conversaAtiva);
      // Dispara evento global pra outras views (ViewerProposta, Pipeline, etc.) reagirem
      window.dispatchEvent(new CustomEvent('hydra-acao-aplicada', {
        detail: { tool: data?.resultado ? findToolName(mensagens, mensagemId, acaoId) : null, resultado: data?.resultado },
      }));
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
      // Recusa (409 "já está sendo aplicado", 504 do gateway…): o card mostra o
      // estado real — senão seguia com "✓ Aplicar" enquanto outro pedido gravava.
      if (conversaAtiva) await carregarMensagens(conversaAtiva);
    } finally {
      aplicandoRef.current.delete(chave);
    }
  }

  // Prioridades pra garantir que pré-requisitos sejam aplicados antes dos dependentes.
  // (mesma ordem do backend pra Telegram)
  const PRIORIDADE_TOOLS_FRONT: Record<string, number> = {
    salvar_memoria: 5, salvar_exemplo_redacao: 5,
    criar_cliente: 10, criar_contato: 20, atualizar_contato: 25,
    criar_negocio: 30, atualizar_negocio: 35,
    criar_composicao: 40, criar_atividade: 50,
    gerar_proposta: 60, vincular_composicao_a_proposta: 70, atualizar_proposta_documento: 75,
    editar_trecho_proposta: 76,
  };

  async function aplicarTodas(mensagemId: string, acoes: AcaoProposta[]) {
    // Edição de proposta fica de fora (TOOLS_EDICAO_PROPOSTA): é aplicada um a um, lendo a prévia.
    const pendentes = acoes.filter(a => a.status === 'pending' && !TOOLS_EDICAO_PROPOSTA.has(a.tool));
    // Ordena por dependência: criar_cliente → criar_contato → criar_negocio → ... → gerar_proposta
    const ordenadas = [...pendentes].sort((a, b) => {
      const pa = PRIORIDADE_TOOLS_FRONT[a.tool] ?? 999;
      const pb = PRIORIDADE_TOOLS_FRONT[b.tool] ?? 999;
      return pa - pb;
    });
    for (const a of ordenadas) {
      await aplicarAcao(mensagemId, a.id, false, 'pending');
    }
  }

  async function descartarAcao(mensagemId: string, acaoId: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/agentes/acoes/descartar`, { mensagem_id: mensagemId, acao_id: acaoId },
        { headers: { Authorization: token } });
      if (conversaAtiva) await carregarMensagens(conversaAtiva);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
  }

  // Bubble flutuante quando fechado
  if (!aberto) {
    return (
      <button onClick={() => setAberto(true)}
        title={`Falar com o ${config.nome}`}
        style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 1500,
          width: 56, height: 56, borderRadius: '50%', border: 'none',
          background: config.bubbleGradiente,
          color: '#fff', fontSize: 24, cursor: 'pointer',
          boxShadow: config.bubbleSombra,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        {config.icone}
      </button>
    );
  }

  // Drawer aberto — em mobile ocupa tela inteira
  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, zIndex: 1500,
      width: isMobile ? '100vw' : (showSidebar ? 720 : 460),
      maxWidth: isMobile ? '100vw' : '95vw',
      background: '#fff',
      borderLeft: isMobile ? 'none' : '1px solid #e0e0e0',
      boxShadow: isMobile ? 'none' : '-4px 0 24px rgba(0,0,0,0.08)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', background: config.gradiente,
        color: '#fff', display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {config.iconeHeader}
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 14 }}>{config.nome}</strong>
          <div style={{ fontSize: 10, opacity: 0.85 }}>{config.subtitulo}</div>
        </div>
        <button onClick={() => setShowSidebar(s => !s)}
          title={showSidebar ? 'Ocultar histórico' : 'Mostrar histórico'}
          style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: 4 }}>
          {showSidebar ? '◀' : '▶'}
        </button>
        <button onClick={novaConversa} title="Nova conversa"
          style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: 4 }}>
          ＋
        </button>
        <button onClick={() => setAberto(false)} title="Fechar"
          style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, padding: 4 }}>
          ×
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Sidebar de conversas — em mobile vira full-width quando aberta */}
        {showSidebar && (
          <div style={{
            width: isMobile ? '100%' : 240,
            borderRight: isMobile ? 'none' : '0.5px solid #e0e0e0',
            overflow: 'auto', background: '#FAFBFF',
            position: isMobile ? 'absolute' : 'static',
            top: isMobile ? 0 : 'auto', left: isMobile ? 0 : 'auto',
            bottom: isMobile ? 0 : 'auto', right: isMobile ? 0 : 'auto',
            zIndex: isMobile ? 5 : 'auto',
          }}>
            <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase' }}>
              Histórico ({conversas.length})
            </div>
            {conversas.length === 0 && (
              <div style={{ padding: 16, fontSize: 11, color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>
                Nenhuma conversa ainda
              </div>
            )}
            {conversas.map(c => (
              <div key={c.id}
                onClick={() => setConversaAtiva(c.id)}
                style={{
                  padding: '8px 12px', cursor: 'pointer', borderBottom: '0.5px solid #f0f0f0',
                  background: conversaAtiva === c.id ? '#E7ECFD' : 'transparent',
                  fontSize: 11, position: 'relative',
                }}>
                <div style={{ fontWeight: 500, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.titulo || 'Sem título'}
                </div>
                <div style={{ fontSize: 9, color: '#888', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{new Date(c.atualizado_em).toLocaleDateString('pt-BR')}</span>
                  <span>{c.n_mensagens} msg</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); excluirConversa(c.id); }}
                  title="Excluir"
                  style={{
                    position: 'absolute', right: 6, top: 6, background: 'transparent', border: 'none',
                    fontSize: 11, cursor: 'pointer', color: '#aaa', padding: 2,
                  }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Área de chat — aceita arquivos arrastados (drag-and-drop) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}
          onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
          {arrastando && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 20, pointerEvents: 'none',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 8, background: 'rgba(231,236,253,0.92)', border: `2px dashed ${config.corPrimaria}`,
              borderRadius: 8, margin: 6,
            }}>
              <div style={{ fontSize: 34 }}>📎</div>
              <strong style={{ color: config.corPrimaria, fontSize: 14 }}>Solte os arquivos aqui</strong>
              <span style={{ fontSize: 11, color: '#555' }}>PDF, KML/KMZ, Shapefile (.zip), GeoJSON e imagens</span>
            </div>
          )}
          <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: 14, background: '#FAFBFF' }}>
            {mensagens.length === 0 && !enviando && (
              <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                <div style={{ marginBottom: 10, color: config.corPrimaria }}>{config.iconeVazio}</div>
                <strong style={{ fontSize: 14, color: config.corPrimaria }}>{config.dicaTitulo}</strong>
                <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {config.dicaTexto}
                </div>
                <div style={{ fontSize: 11, marginTop: 14, padding: 10, background: '#fff', borderRadius: 6, textAlign: 'left', border: '0.5px dashed #ddd' }}>
                  <strong style={{ color: config.corPrimaria }}>Exemplos:</strong>
                  <ul style={{ margin: '6px 0 0 20px', padding: 0, fontSize: 11 }}>
                    {config.exemplos.map((ex, i) => <li key={i}>{ex}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {mensagens.map((m) => <MessageItem key={m.id} mensagem={m}
              onAplicar={(acaoId, status) => aplicarAcao(m.id, acaoId, true, status)}
              onDescartar={(acaoId) => descartarAcao(m.id, acaoId)}
              onAplicarTodas={(acoes) => aplicarTodas(m.id, acoes)}
              onPedirDeNovo={(texto) => { setInput(texto); setTimeout(() => textareaRef.current?.focus(), 0); }} />)}

            {enviando && (
              <div style={{
                padding: '10px 12px', fontSize: 11, color: config.corPrimaria,
                background: '#FAFBFF', border: '0.5px solid #CDD4EA', borderRadius: 6,
                margin: '8px 0', display: 'flex', alignItems: 'center', gap: 10,
                position: 'sticky', bottom: 0,
              }}>
                <span className="hydra-dots" style={{ display: 'inline-flex', gap: 3 }}>
                  <span className="hydra-dot" style={{ animationDelay: '0s' }} />
                  <span className="hydra-dot" style={{ animationDelay: '0.2s' }} />
                  <span className="hydra-dot" style={{ animationDelay: '0.4s' }} />
                </span>
                <span style={{ fontWeight: 500 }}>{config.nome} está pensando</span>
                <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 11, color: '#666', fontWeight: 600 }}>
                  {formatarDuracao(tempoEnvio)}
                </span>
                <button onClick={pararDwight}
                  title={`Parar ${config.nome} (descartará a resposta em produção)`}
                  style={{
                    padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4,
                    border: '0.5px solid #B91626', background: '#fff', color: '#B91626', cursor: 'pointer',
                  }}>
                  ⏹ Parar
                </button>
              </div>
            )}

            {erro && (
              <div style={{ margin: 8, padding: 10, background: '#FEE2E2', border: '0.5px solid #B91626', borderRadius: 5, fontSize: 11, color: '#B91626' }}>
                {erro} <button onClick={() => setErro(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#B91626' }}>×</button>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{ borderTop: '0.5px solid #e0e0e0', padding: 10, background: '#fff' }}>
            {anexos.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {anexos.map((a, i) => (
                  <div key={i} style={{ fontSize: 11, padding: '4px 8px', background: '#E7ECFD', borderRadius: 4, color: '#020D7F', display: 'flex', alignItems: 'center', gap: 6 }}>
                    📎 {a.nome}
                    <button onClick={() => setAnexos(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#020D7F' }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
              <button onClick={() => fileRef.current?.click()} title="Anexar PDF"
                disabled={enviando}
                style={{
                  padding: '8px 10px', borderRadius: 5, border: '0.5px solid #ddd',
                  background: '#fff', cursor: enviando ? 'not-allowed' : 'pointer', fontSize: 14,
                }}>📎</button>
              <input ref={fileRef} type="file"
                accept=".pdf,application/pdf,.kml,.kmz,.zip,.geojson,.json,image/*,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                multiple
                onChange={e => { onAnexar(e.target.files); e.target.value = ''; }}
                style={{ display: 'none' }} />
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
                }}
                placeholder={config.placeholder}
                disabled={enviando}
                rows={2}
                style={{
                  flex: 1, padding: '8px 10px', fontSize: 12, borderRadius: 5,
                  border: '0.5px solid #ddd', resize: 'none', fontFamily: 'inherit',
                  outline: 'none', lineHeight: 1.45,
                  minHeight: 40,
                }} />
              <button onClick={enviar} disabled={enviando || (!input.trim() && anexos.length === 0)}
                style={{
                  padding: '8px 14px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                  border: 'none', background: config.corPrimaria, color: '#fff',
                  cursor: enviando ? 'not-allowed' : 'pointer', opacity: enviando ? 0.5 : 1,
                }}>
                {enviando ? '…' : 'Enviar'}
              </button>
            </div>
            <div style={{ fontSize: 9, color: '#aaa', marginTop: 4 }}>
              Enter envia · Shift+Enter quebra linha · PDF/KMZ/KML/SHP/imagens até 25MB
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hydraPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        .hydra-pulse { animation: hydraPulse 1.4s ease-in-out infinite; }
        @keyframes hydraDot {
          0%, 80%, 100% { transform: scale(0.5); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        .hydra-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #0930D0; display: inline-block;
          animation: hydraDot 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function MessageItem({ mensagem, onAplicar, onDescartar, onAplicarTodas, onPedirDeNovo }: {
  mensagem: Mensagem;
  onAplicar: (acaoId: string, status: string) => void;
  onDescartar: (acaoId: string) => void;
  onAplicarTodas: (acoes: AcaoProposta[]) => void;
  onPedirDeNovo?: (texto: string) => void;
}) {
  const isUser = mensagem.papel === 'user';
  const acoes = mensagem.acoes_propostas || [];
  // A tentativa parcial continua no histórico para auditoria, mas some da
  // conversa quando uma ação íntegra a substitui na recuperação automática.
  const acoesVisiveis = filtrarAcoesVisiveis(acoes);
  const pendentes = acoesVisiveis.filter(a => a.status === 'pending');
  // "Aplicar todas" só conta o que ele de fato aplica: edição de proposta fica de fora.
  const aplicaveisEmLote = pendentes.filter(a => !TOOLS_EDICAO_PROPOSTA.has(a.tool));
  const edicoesForaDoLote = pendentes.length - aplicaveisEmLote.length;
  const [traceAberto, setTraceAberto] = useState(false);
  const temTrace = !isUser && Array.isArray(mensagem.passos) && mensagem.passos.length > 0;

  return (
    <div style={{
      marginBottom: 12,
      display: 'flex',
      flexDirection: isUser ? 'row-reverse' : 'row',
    }}>
      <div style={{
        maxWidth: '88%',
        padding: '10px 14px',
        borderRadius: 10,
        background: isUser ? '#0930D0' : '#fff',
        color: isUser ? '#fff' : '#222',
        fontSize: 12, lineHeight: 1.5,
        border: isUser ? 'none' : '0.5px solid #e0e0e0',
        whiteSpace: 'pre-wrap',
      }}>
        {mensagem.conteudo}
        {Array.isArray(mensagem.anexos) && mensagem.anexos.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 10, opacity: 0.8 }}>
            {mensagem.anexos.map((a: any, i: number) => <span key={i} style={{ marginRight: 8 }}>📎 {a.nome}</span>)}
          </div>
        )}
        {/* Cards de ações propostas */}
        {!isUser && acoesVisiveis.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {acoesVisiveis.map(a => <AcaoCard key={a.id} acao={a}
              onAplicar={() => onAplicar(a.id, a.status)}
              onDescartar={() => onDescartar(a.id)}
              onPedirDeNovo={onPedirDeNovo} />)}
            {aplicaveisEmLote.length > 1 && (
              <button onClick={() => onAplicarTodas(acoesVisiveis)}
                title={edicoesForaDoLote > 0 ? 'Edições de proposta ficam para você conferir uma a uma' : undefined}
                style={{
                  marginTop: 4, padding: '6px 12px', fontSize: 11, fontWeight: 600, borderRadius: 5,
                  border: 'none', background: '#0a7f4a', color: '#fff', cursor: 'pointer',
                }}>
                ✓ Aplicar todas ({aplicaveisEmLote.length}){edicoesForaDoLote > 0 ? ` — ${edicoesForaDoLote === 1 ? 'a edição de proposta fica' : `${edicoesForaDoLote} edições de proposta ficam`} para você conferir` : ''}
              </button>
            )}
          </div>
        )}
        {/* Trace expansível: passo a passo do ciclo agentic */}
        {temTrace && (
          <div style={{ marginTop: 10, paddingTop: 6, borderTop: '0.5px solid #f0f0f0', whiteSpace: 'normal' }}>
            <button onClick={() => setTraceAberto(v => !v)}
              style={{ fontSize: 10, padding: '3px 8px', borderRadius: 3, cursor: 'pointer',
                border: '0.5px solid #ddd', background: '#fff', color: '#666',
                display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{traceAberto ? '▼' : '▶'}</span>
              <span>🔍 Ver passo a passo</span>
              {mensagem.duracao_ms && (
                <span style={{ color: '#888' }}>· {(mensagem.duracao_ms / 1000).toFixed(1)}s</span>
              )}
              {(mensagem.tokens_in || mensagem.tokens_out) && (
                <span style={{ color: '#888' }}>
                  · {(mensagem.tokens_in || 0) + (mensagem.tokens_out || 0)} tok
                </span>
              )}
            </button>
            {traceAberto && <TraceSteps passos={mensagem.passos || []} />}
          </div>
        )}
      </div>
    </div>
  );
}

// Formata trace em texto plano pra copiar pro clipboard
function formatarTraceTexto(passos: any[]): string {
  const linhas: string[] = [];
  for (const p of passos) {
    if (p.etapa === 'bedrock_call') {
      linhas.push(`\n⚙️ Iter ${p.iter} · Bedrock · ${p.duracao_ms}ms · ${p.tokens_in}→${p.tokens_out} tok`);
      const parada = descreverParada(p);
      if (parada) linhas.push(parada);
      if (p.texto_parcial) linhas.push(`💬 "${p.texto_parcial}${p.texto_parcial.length >= 300 ? '…' : ''}"`);
      const tools = (p.tool_uses || []) as any[];
      if (tools.length > 0) {
        linhas.push('Decidiu chamar:');
        for (const t of tools) {
          const params = Object.entries(t.input || {})
            .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 60)}`)
            .join(', ');
          linhas.push(`  ${t.tipo === 'leitura' ? '🔍' : '✏️'} ${t.nome}${params ? `(${params})` : ''}`);
        }
      }
    } else if (p.etapa === 'tool_use') {
      linhas.push(`${p.sucesso ? '✓' : '✗'} ${p.tool} · ${p.duracao_ms}ms`);
      if (p.input_truncado && p.input_truncado !== '{}') linhas.push(`  in: ${p.input_truncado}`);
      if (p.output_truncado) linhas.push(`  out: ${p.output_truncado}`);
      if (p.erro) linhas.push(`  ⚠ ${p.erro}`);
    } else if (p.etapa === 'auto_continuar') {
      linhas.push(`\n⟳ Auto-continuação: ${p.motivo}`);
      if (p.frase_detectada) linhas.push(`  Frase: "${p.frase_detectada}"`);
    } else if (descreverPassoDeControle(p)) {
      linhas.push(`\n${descreverPassoDeControle(p)}`);
    } else if (p.etapa === 'final') {
      linhas.push(`\n✓ Final · ${p.iter_total} iter(s) · ${(p.duracao_ms_total / 1000).toFixed(1)}s · ${p.tokens_in_total}→${p.tokens_out_total} tok · ${p.n_acoes_propostas} ação(ões) propostas`);
    }
  }
  return linhas.join('\n');
}

// Renderiza a sequência de passos do ciclo agentic — visualização em timeline
function TraceSteps({ passos }: { passos: any[] }) {
  const [copiado, setCopiado] = useState(false);
  function copiar() {
    const txt = formatarTraceTexto(passos);
    navigator.clipboard.writeText(txt).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    });
  }
  return (
    <div style={{ marginTop: 8, fontSize: 11, fontFamily: 'monospace',
      background: '#FAFBFF', border: '0.5px solid #e0e0e0', borderRadius: 4,
      padding: 10, maxHeight: 360, overflowY: 'auto', position: 'relative' }}>
      <button onClick={copiar} title="Copiar trace formatado pra clipboard"
        style={{
          position: 'absolute', top: 6, right: 6, zIndex: 2,
          padding: '3px 9px', fontSize: 10, fontWeight: 600,
          border: '0.5px solid #0930D0',
          background: copiado ? '#0a7f4a' : '#fff',
          color: copiado ? '#fff' : '#0930D0',
          borderRadius: 3, cursor: 'pointer',
        }}>
        {copiado ? '✓ Copiado!' : '📋 Copiar'}
      </button>
      {passos.map((p, i) => {
        if (p.etapa === 'bedrock_call') {
          const tools = (p.tool_uses || []) as any[];
          const cacheRead = p.cache_read || 0;
          const cacheWrite = p.cache_write || 0;
          return (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ color: '#0930D0', fontWeight: 600, fontSize: 10 }}>
                ⚙️ Iter {p.iter} · Bedrock · {p.duracao_ms}ms · {p.tokens_in}→{p.tokens_out} tok
                {cacheRead > 0 && (
                  <span style={{ marginLeft: 6, color: '#0a7f4a', fontWeight: 500 }}>
                    · 💾 cache: {cacheRead} hit{cacheWrite > 0 ? ` / ${cacheWrite} write` : ''}
                  </span>
                )}
                {cacheRead === 0 && cacheWrite > 0 && (
                  <span style={{ marginLeft: 6, color: '#888', fontWeight: 500 }}>
                    · 💾 cache: {cacheWrite} write (1ª vez)
                  </span>
                )}
                {descreverParada(p) && (
                  <span style={{ marginLeft: 6, color: '#B91626', fontWeight: 600 }}>
                    · {descreverParada(p)}
                  </span>
                )}
              </div>
              {p.texto_parcial && (
                <div style={{ color: '#666', fontSize: 10, marginTop: 2,
                  padding: '4px 6px', background: '#fff', borderRadius: 3,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  💬 "{p.texto_parcial}{p.texto_parcial.length >= 300 ? '…' : ''}"
                </div>
              )}
              {tools.length > 0 && (
                <div style={{ marginTop: 3, marginLeft: 12, color: '#555', fontSize: 10 }}>
                  Decidiu chamar:
                  {tools.map((t, j) => (
                    <span key={j} style={{ display: 'block', marginLeft: 8, marginTop: 2 }}>
                      <span style={{
                        color: t.tipo === 'leitura' ? '#0a7f4a' : '#B07500',
                        fontWeight: 600,
                      }}>
                        {t.tipo === 'leitura' ? '🔍' : '✏️'} {t.nome}{t.cortado ? ' ⚠ cortada' : ''}
                      </span>
                      {Object.keys(t.input || {}).length > 0 && (
                        <span style={{ color: '#888' }}>
                          ({Object.entries(t.input).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v).slice(0, 30) : String(v).slice(0, 30)}`).join(', ')})
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        }
        if (p.etapa === 'tool_use') {
          return (
            <div key={i} style={{ marginBottom: 6, marginLeft: 12,
              padding: '5px 8px',
              background: p.sucesso ? '#E8F5EE' : '#FEE7E7',
              borderRadius: 3 }}>
              <div style={{ fontSize: 10,
                color: p.sucesso ? '#0a7f4a' : '#B91626', fontWeight: 600 }}>
                {p.sucesso ? '✓' : '✗'} {p.tool} · {p.duracao_ms}ms
              </div>
              {p.input_truncado && p.input_truncado !== '{}' && (
                <div style={{ color: '#666', fontSize: 9, marginTop: 2,
                  wordBreak: 'break-all' }}>
                  <strong>in:</strong> {p.input_truncado}
                </div>
              )}
              {p.output_truncado && (
                <div style={{ color: '#444', fontSize: 9, marginTop: 2,
                  wordBreak: 'break-all',
                  maxHeight: 60, overflow: 'hidden' }}>
                  <strong>out:</strong> {p.output_truncado}
                </div>
              )}
              {p.erro && (
                <div style={{ color: '#B91626', fontSize: 9, marginTop: 2 }}>
                  ⚠ {p.erro}
                </div>
              )}
            </div>
          );
        }
        if (p.etapa === 'auto_continuar') {
          return (
            <div key={i} style={{ marginBottom: 6, padding: '5px 8px',
              background: '#FFF8E8', border: '0.5px dashed #B07500', borderRadius: 3,
              fontSize: 10, color: '#664400' }}>
              ⟳ <strong>Auto-continuação</strong>: {p.motivo}
              {p.frase_detectada && <div style={{ marginTop: 2, fontStyle: 'italic' }}>
                Frase detectada: "{p.frase_detectada}"
              </div>}
            </div>
          );
        }
        if (descreverPassoDeControle(p)) {
          // Recuperação automática de corte/ação incompleta, ou parada por falta de tempo.
          const semTempo = p.etapa === 'orcamento_tempo';
          return (
            <div key={i} style={{ marginBottom: 6, padding: '5px 8px',
              background: semTempo ? '#FDECEC' : '#FFF8E8', border: `0.5px dashed ${semTempo ? '#B91626' : '#B07500'}`, borderRadius: 3,
              fontSize: 10, color: semTempo ? '#7a1010' : '#664400' }}>
              {descreverPassoDeControle(p)}
            </div>
          );
        }
        if (p.etapa === 'final') {
          return (
            <div key={i} style={{ marginTop: 6, paddingTop: 6,
              borderTop: '0.5px dashed #ddd', fontSize: 10, color: '#666' }}>
              ✓ Final · {p.iter_total} iter(s) · {(p.duracao_ms_total / 1000).toFixed(1)}s · {p.tokens_in_total}→{p.tokens_out_total} tok · {p.n_acoes_propostas} ação(ões) propostas
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function AcaoCard({ acao, onAplicar, onDescartar, onPedirDeNovo }: {
  acao: AcaoProposta;
  onAplicar: () => void;
  onDescartar: () => void;
  onPedirDeNovo?: (texto: string) => void;
}) {
  const isPending = acao.status === 'pending';
  const isApplied = acao.status === 'applied';
  const isDismissed = acao.status === 'dismissed';
  const isError = acao.status === 'erro';
  const isTruncada = acao.status === 'truncada';
  const isInvalida = acao.status === 'invalida';
  const isCortada = isTruncada || isInvalida;
  // Reservado pelo servidor: recente = gravando agora, sem botão (o 2º pedido
  // receberia 409); velho = não confirmou, e aí vale o mesmo caminho do erro.
  const aplicando = situacaoAplicando(acao);
  const isEmAndamento = aplicando === 'em_andamento';
  const isSemConfirmacao = aplicando === 'sem_confirmacao';

  // Prévia estruturada da edição de proposta: o que muda e quanto. Vermelho
  // quando encolhe demais ou quando a proposta já foi enviada ao cliente.
  // O alarme (⚠ encolhe / já enviada) é para DECIDIR: só enquanto pendente ou cortada.
  const previaProposta = acao.tool === 'atualizar_proposta_documento'
    ? resumirAtualizacaoProposta(acao.input, acao.previa, { alarmar: isPending || isCortada }) : null;
  const previaComposicao = acao.tool === 'criar_composicao' && ehPreviaCriacaoComposicao(acao.previa)
    ? acao.previa : null;
  const alerta = !!previaProposta?.alerta;

  const corBorda = isApplied ? '#0a7f4a' : isError ? '#B91626' : (isCortada || isSemConfirmacao) ? '#B07500' : isDismissed ? '#aaa' : alerta ? '#B91626' : '#0930D0';
  const corFundo = isApplied ? '#0a7f4a15' : isError ? '#B9162615' : (isCortada || isSemConfirmacao) ? '#B0750012' : isDismissed ? '#F0F1F5' : alerta ? '#B9162608' : '#FAFBFF';

  const pedirDeNovo = textoPedirDeNovo(acao.tool, labelTool(acao.tool), acao.input?.numero_proposta,
    { status: acao.status, candidatos: acao.candidatos_numero, motivo_codigo: acao.motivo_codigo });

  return (
    <div style={{
      padding: 8, borderRadius: 5, border: `0.5px solid ${corBorda}`,
      background: corFundo, fontSize: 11,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 9, padding: '1px 6px', borderRadius: 3,
          background: corBorda, color: '#fff', fontWeight: 600, textTransform: 'uppercase',
        }}>
          {labelTool(acao.tool)}
        </span>
        {isApplied && <span style={{ fontSize: 11, color: '#0a7f4a' }}>✓ aplicada</span>}
        {isDismissed && <span style={{ fontSize: 11, color: '#aaa' }}>descartada</span>}
        {isError && <span style={{ fontSize: 11, color: '#B91626' }}>⚠ erro</span>}
        {isTruncada && <span style={{ fontSize: 11, color: '#B07500', fontWeight: 600 }}>⚠ chegou cortada</span>}
        {isInvalida && <span style={{ fontSize: 11, color: '#B07500', fontWeight: 600 }}>
          {rotuloBloqueio(acao.motivo_codigo)}
        </span>}
        {isEmAndamento && <span style={{ fontSize: 11, color: '#0930D0' }}>⏳ aplicando…</span>}
        {isSemConfirmacao && <span style={{ fontSize: 11, color: '#B07500', fontWeight: 600 }}>⚠ não confirmou</span>}
        {previaProposta && previaProposta.n_campos > 1 && (
          <span style={{ fontSize: 10, color: '#444' }}>· {previaProposta.n_campos} campos num card só</span>
        )}
        {acao.numero_proposta_inferido && (
          <span style={{ fontSize: 10, color: '#666', fontStyle: 'italic' }}>· proposta inferida da conversa</span>
        )}
        {acao.recuperou_truncamento && (
          <span style={{ fontSize: 10, color: '#0a7f4a', fontStyle: 'italic' }}>· ↻ refeita automaticamente após um corte</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: alerta ? '#B91626' : '#222', lineHeight: 1.5, fontWeight: alerta ? 600 : 400 }}>
        {previaProposta
          ? (previaProposta.linhas.length > 2   /* com as linhas de mudança, quase sempre */
            ? previaProposta.linhas.map((l, i) => <div key={i}>{i === 0 ? l : `• ${l}`}</div>)
            : previaProposta.texto)
          : previaComposicao
            ? resumirCriacaoComposicao(acao.input, previaComposicao)
            : acao.tool === 'vincular_composicao_a_proposta'
              ? `Composição "${acao.input?.composicao_nome_ou_id ?? '?'}" → proposta ${descreverAlvoProposta(acao.input?.numero_proposta, acao.previa) || acao.input?.numero_proposta || '?'}`
              : resumirInput(acao.tool, acao.input)}
      </div>
      {previaComposicao && <DetalhesCriacaoComposicao previa={previaComposicao} />}
      {previaProposta && previaProposta.avisos.length > 0 && (
        <div style={{ fontSize: 10, color: '#B91626', marginTop: 4 }}>
          {previaProposta.avisos.map((a, i) => <div key={i}>⚠ {a}</div>)}
        </div>
      )}
      {isCortada && (
        <div style={{ fontSize: 10, color: '#7a5200', marginTop: 4, fontStyle: 'italic' }}>
          {acao.motivo || 'A ação chegou incompleta e não pode ser aplicada. Peça de novo ao Dwight, mais curta.'}
        </div>
      )}
      {isSemConfirmacao && (
        <div style={{ fontSize: 10, color: '#7a5200', marginTop: 4, fontStyle: 'italic' }}>
          {textoAplicacaoSemConfirmacao(acao.aplicando_desde)}
        </div>
      )}
      {isError && acao.resultado?.erro && (
        <div style={{ fontSize: 10, color: '#B91626', marginTop: 4, fontStyle: 'italic' }}>
          {acao.resultado.erro}
        </div>
      )}
      {isApplied && acao.resultado && (
        <div style={{ fontSize: 10, color: '#0a7f4a', marginTop: 4, fontStyle: 'italic' }}>
          {resumirResultado(acao.tool, acao.resultado)}
        </div>
      )}
      {isPending && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
          <button onClick={onAplicar}
            style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 4,
              border: 'none', background: alerta ? '#B91626' : '#0a7f4a', color: '#fff', cursor: 'pointer' }}>
            {alerta ? '✓ Aplicar mesmo assim' : '✓ Aplicar'}
          </button>
          <button onClick={onDescartar}
            style={{ padding: '4px 10px', fontSize: 10, borderRadius: 4,
              border: '0.5px solid #aaa', background: '#fff', color: '#666', cursor: 'pointer' }}>
            Descartar
          </button>
        </div>
      )}
      {(isError || isSemConfirmacao) && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
          {TOOLS_RETENTAVEIS.has(acao.tool) ? (
            <button onClick={onAplicar} title="Executa a mesma ação de novo (o servidor aceita retentativa de ação com erro)"
              style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 4,
                border: 'none', background: '#0930D0', color: '#fff', cursor: 'pointer' }}>
              ↻ Tentar de novo
            </button>
          ) : onPedirDeNovo ? (
            <button onClick={() => onPedirDeNovo(isSemConfirmacao
              ? `A ação "${labelTool(acao.tool)}" começou a ser aplicada e não confirmou. Confira o que já existe e proponha de novo só o que faltar.`
              : `A ação "${labelTool(acao.tool)}" deu erro (${acao.resultado?.erro || 'sem detalhe'}). Confira o que já existe e proponha de novo.`)}
              title="Ações que criam registro não repetem às cegas — o Dwight confere o que já existe antes de propor de novo"
              style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 4,
                border: 'none', background: '#0930D0', color: '#fff', cursor: 'pointer' }}>
              ✎ Pedir de novo
            </button>
          ) : null}
          {/* "Não confirmou" pode ter gravado: descartar esconderia isso (o servidor também recusa). */}
          {isError && <button onClick={onDescartar}
            style={{ padding: '4px 10px', fontSize: 10, borderRadius: 4,
              border: '0.5px solid #aaa', background: '#fff', color: '#666', cursor: 'pointer' }}>
            Descartar
          </button>}
        </div>
      )}
      {isCortada && onPedirDeNovo && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
          <button onClick={() => onPedirDeNovo(pedirDeNovo)}
            title="Preenche a mensagem para você conferir antes de enviar"
            style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 4,
              border: 'none', background: '#B07500', color: '#fff', cursor: 'pointer' }}>
            ✎ Pedir de novo
          </button>
        </div>
      )}
    </div>
  );
}

function DetalhesCriacaoComposicao({ previa }: { previa: PreviaCriacaoComposicao }) {
  return (
    <details style={{ marginTop: 6 }}>
      <summary style={{ cursor: 'pointer', color: '#0930D0', fontWeight: 600 }}>
        Conferir {previa.grupos.length} grupo(s) e {previa.itens.length} item(ns)
      </summary>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ border: '0.5px solid #CDD4EA', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ padding: '4px 6px', background: '#E7ECFD', color: '#020D7F', fontWeight: 600 }}>
            Grupos de venda
          </div>
          {previa.grupos.map((grupo, i) => (
            <div key={`${grupo.descricao}-${i}`} style={{
              padding: '4px 6px', display: 'flex', gap: 8, justifyContent: 'space-between',
              borderTop: i === 0 ? 'none' : '0.5px solid #E5E7EF', flexWrap: 'wrap',
            }}>
              <span>{grupo.descricao || 'Grupo sem descrição'} · {formatarNumeroComposicao(grupo.quantidade)} un.</span>
              <span>
                {formatarDinheiroComposicao(grupo.valor_unitario)} por un. · <strong>{formatarDinheiroComposicao(grupo.total_venda)}</strong>
              </span>
            </div>
          ))}
        </div>

        <div style={{ overflowX: 'auto', border: '0.5px solid #CDD4EA', borderRadius: 4 }}>
          <table style={{ width: '100%', minWidth: 650, borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr style={{ background: '#F0F1F5', color: '#020D7F' }}>
                <th style={thPreviaComposicao}>Item</th>
                <th style={thPreviaComposicao}>Origem</th>
                <th style={{ ...thPreviaComposicao, textAlign: 'right' }}>Qtd.</th>
                <th style={{ ...thPreviaComposicao, textAlign: 'right' }}>Coef.</th>
                <th style={thPreviaComposicao}>Unid.</th>
                <th style={{ ...thPreviaComposicao, textAlign: 'right' }}>Custo unit.</th>
                <th style={{ ...thPreviaComposicao, textAlign: 'right' }}>Markup</th>
              </tr>
            </thead>
            <tbody>
              {previa.itens.map((item, i) => (
                <tr key={`${item.descricao}-${i}`} style={{ borderTop: '0.5px solid #E5E7EF' }}>
                  <td style={tdPreviaComposicao}>{item.descricao || 'Item sem descrição'}</td>
                  <td style={tdPreviaComposicao}>{item.origem === 'catalogo' ? 'catálogo' : 'livre'}</td>
                  <td style={{ ...tdPreviaComposicao, textAlign: 'right' }}>{formatarNumeroComposicao(item.quantidade)}</td>
                  <td style={{ ...tdPreviaComposicao, textAlign: 'right', fontWeight: item.coeficiente === 0 ? 700 : 400 }}>
                    {formatarNumeroComposicao(item.coeficiente)}
                  </td>
                  <td style={tdPreviaComposicao}>{item.unidade || '—'}</td>
                  <td style={{ ...tdPreviaComposicao, textAlign: 'right' }}>{formatarDinheiroComposicao(item.valor_unitario)}</td>
                  <td style={{ ...tdPreviaComposicao, textAlign: 'right' }}>{formatarNumeroComposicao(item.markup_pct, 2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

const thPreviaComposicao: CSSProperties = { padding: '4px 6px', textAlign: 'left', whiteSpace: 'nowrap' };
const tdPreviaComposicao: CSSProperties = { padding: '4px 6px', whiteSpace: 'nowrap' };

// Acha o nome da tool dado o id da ação na lista de mensagens (pra dispatchar event tipado)
function findToolName(mensagens: Mensagem[], mensagemId: string, acaoId: string): string | null {
  const msg = mensagens.find(m => m.id === mensagemId);
  if (!msg) return null;
  const acao = (msg.acoes_propostas || []).find((a: any) => a.id === acaoId);
  return acao?.tool || null;
}

function labelTool(tool: string): string {
  const map: Record<string, string> = {
    buscar_cliente: 'Buscar empresa',
    criar_cliente: 'Criar empresa',
    buscar_contato: 'Buscar contato',
    criar_contato: 'Criar contato',
    atualizar_contato: 'Atualizar contato',
    buscar_negocio: 'Buscar negócio',
    criar_negocio: 'Criar negócio',
    atualizar_negocio: 'Atualizar negócio',
    criar_atividade: 'Criar atividade',
    gerar_proposta: 'Gerar proposta',
    atualizar_proposta_documento: 'Atualizar proposta',
    obter_proposta_documento: 'Ler proposta',
    editar_trecho_proposta: 'Editar trecho da proposta',
    consultar_pipeline: 'Consultar pipeline',
    consultar_memoria: 'Consultar memória',
    salvar_memoria: 'Salvar memória',
    registrar_lacuna: 'Registrar lacuna',
    consultar_exemplos_redacao: 'Consultar exemplos',
    salvar_exemplo_redacao: 'Salvar exemplo de redação',
    consultar_custos_unitarios: 'Consultar custos',
    consultar_servicos_produtos: 'Consultar serviços',
    consultar_composicao_referencia: 'Consultar composições anteriores',
    consultar_proposta_referencia: 'Consultar propostas anteriores',
    criar_composicao: 'Criar composição',
    calcular_distancia: 'Calcular distância',
    vincular_composicao_a_proposta: 'Vincular composição à proposta',
  };
  return map[tool] || tool;
}

function resumirInput(tool: string, input: any): string {
  if (tool === 'buscar_cliente') return `"${input.query}"`;
  if (tool === 'criar_cliente') return `${input.razao_social}${input.cnpj ? ` · CNPJ ${input.cnpj}` : ''}${input.segmento ? ` · ${input.segmento}` : ''}`;
  if (tool === 'buscar_contato') return `"${input.query}"${input.cliente_razao_social ? ` em ${input.cliente_razao_social}` : ''}`;
  if (tool === 'criar_contato') return `${input.nome}${input.cargo ? ` (${input.cargo})` : ''} em ${input.cliente_razao_social}`;
  if (tool === 'atualizar_contato') {
    const partes = [];
    if (input.email !== undefined) partes.push(`email`);
    if (input.telefone !== undefined) partes.push(`telefone`);
    if (input.cargo !== undefined) partes.push(`cargo`);
    return `Contato ${(input.contato_id || '').slice(0,8)} · ${partes.join(', ')}`;
  }
  if (tool === 'buscar_negocio') return `"${input.query}"`;
  if (tool === 'criar_negocio') return `${input.titulo} · ${input.cliente_razao_social}${input.valor_estimado ? ` · R$ ${Number(input.valor_estimado).toLocaleString('pt-BR')}` : ''}${input.area ? ` · ${input.area}` : ''}`;
  if (tool === 'atualizar_negocio') {
    const partes = [];
    if (input.status) partes.push(`status → ${input.status}`);
    if (input.valor_estimado != null) partes.push(`valor → R$ ${Number(input.valor_estimado).toLocaleString('pt-BR')}`);
    if (input.observacoes) partes.push(`+ obs`);
    return `negócio #${input.negocio_id} · ${partes.join(', ')}`;
  }
  if (tool === 'criar_atividade') return `${input.descricao} (em "${input.negocio_titulo_ou_id}")${input.prazo ? ` · ${input.prazo}` : ''}`;
  if (tool === 'criar_composicao') {
    const n = (input.itens || []).length;
    const neg = input.negocio_titulo_ou_id ? ` para "${input.negocio_titulo_ou_id}"` : ' (genérica)';
    return `${input.nome}${neg} · ${n} item(ns) · markup ${input.markup_padrao_pct}%`;
  }
  // Edição de proposta tem resumo próprio (utils/hydraCards): o fallback de
  // 120 caracteres de JSON era o "JSON cortado no meio" que a pessoa via.
  if (tool === 'atualizar_proposta_documento') return resumirAtualizacaoProposta(input, null).texto;
  if (tool === 'gerar_proposta') {
    const t = input.titulo ? ` · "${String(input.titulo).slice(0, 60)}${String(input.titulo).length > 60 ? '…' : ''}"` : '';
    return `Gerar proposta para "${input.negocio_titulo_ou_id}"${t}`;
  }
  return JSON.stringify(input).slice(0, 120);
}

function resumirResultado(tool: string, r: any): string {
  if (r.atualizada?.campos_atualizados) return resumirCamposAtualizados(r.atualizada.campos_atualizados);
  if (r.criada?.numero_proposta) return `Proposta gerada: ${r.criada.numero_proposta}`;
  if (r.criado?.razao_social) return `Empresa criada: ${r.criado.razao_social}`;
  if (r.criado?.nome) return `Contato criado: ${r.criado.nome}`;
  if (r.criado?.titulo && tool === 'criar_negocio') return resumirNegocioCriado(r.criado);
  if (r.criado?.titulo) return `Negócio criado: ${r.criado.titulo}`;
  if (r.criada?.descricao) return `Atividade criada`;
  if (r.atualizado?.titulo) return `Atualizado: ${r.atualizado.titulo}`;
  if (Array.isArray(r.resultados)) return `${r.resultados.length} resultado(s)`;
  return 'Aplicado';
}
